/**
 * SeatBlockDialog
 *
 * Full-screen overlay blocking excess users when seat limit is exceeded.
 *
 * Behavior:
 * - SuperAdmins bypass entirely
 * - Blocked non-admin users see undismissible "your access is limited" message
 * - Admin users (not blocked) see list of blocked users + CTA to buy seats / manage users
 * - Auto-refreshes when `seat:status_changed` socket event fires
 * - Polls seat-status every 60s as fallback
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSocket } from "../contexts/SocketContext";
import { useNavigate } from "react-router-dom";
import {
  Users,
  ShieldAlert,
  UserX,
  Settings,
  RefreshCw,
  Plus,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { accountPath } from "@/utils/accountRoutes";

export default function SeatBlockDialog() {
  const { user, isSuperAdmin, isAdmin, refreshFeatures } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();

  const [seatData, setSeatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Seat purchase state
  const [seatPrice, setSeatPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null); // { success, message } or null
  const priceFetched = useRef(false);

  const showUpgradePrompt = useCallback((detail = {}) => {
    const limit = Number(detail.limit) || 0;
    const current = Number(detail.current) || limit;
    const excess = Math.max(1, Number(detail.excess) || 1);

    setSeatData((prev) => ({
      // Preserva planBaseLimit/extras do último fetch — o evento de upgrade só
      // carrega limit/current/excess, mas base/extras são estáticos da conta.
      ...prev,
      limit,
      current,
      excess,
      isOverLimit: true,
      blockedUserIds: prev?.blockedUserIds || [],
      blockedUsers: prev?.blockedUsers || [],
      isUpgradePrompt: true,
    }));
    setDismissed(false);
    setShowConfirm(false);
    setPurchaseResult(null);
  }, []);

  const fetchSeatStatus = useCallback(async () => {
    try {
      const res = await apiGet("/api/plans/account/seat-status");
      if (!res.ok) {
        // 404 or 403 means endpoint not available — don't block
        if (res.status === 404 || res.status === 403) {
          setSeatData(null);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setSeatData((prev) => {
        // Re-show dialog if seat situation changed (e.g. got worse)
        if (prev && prev.excess !== data.excess) setDismissed(false);
        return data;
      });
    } catch (err) {
      // 402 = billing blocked — not a seat issue, ignore silently
      if (err?.status === 402) return;
      console.warn('[SeatBlockDialog] Seat status fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch seat addon price once when dialog is relevant
  const fetchSeatPrice = useCallback(async () => {
    if (priceFetched.current) return;
    priceFetched.current = true;
    setPriceLoading(true);
    try {
      const res = await apiGet("/api/plans/account/addon-price?featureKey=users.max");
      if (res.ok) {
        const data = await res.json();
        const unitPrice = data.priceDetails?.[0]?.price || data.totalPrice || null;
        setSeatPrice(unitPrice);
      }
    } catch {
      // price not available — button will be disabled
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchSeatStatus();
  }, [fetchSeatStatus]);

  // Listen for seat:status_changed socket events
  useEffect(() => {
    if (!socket) return;

    const handler = () => {
      fetchSeatStatus();
    };

    socket.on("seat:status_changed", handler);
    return () => socket.off("seat:status_changed", handler);
  }, [socket, fetchSeatStatus]);

  useEffect(() => {
    const handler = (event) => {
      showUpgradePrompt(event.detail || {});
    };

    globalThis.addEventListener("seat:upgrade_required", handler);
    return () => globalThis.removeEventListener("seat:upgrade_required", handler);
  }, [showUpgradePrompt]);

  // Polling fallback every 60s when over limit
  useEffect(() => {
    if (!seatData?.isOverLimit) return;

    const interval = setInterval(fetchSeatStatus, 60_000);
    return () => clearInterval(interval);
  }, [seatData?.isOverLimit, fetchSeatStatus]);

  // Fetch seat price when admin sees the dialog
  useEffect(() => {
    if (seatData?.isOverLimit && isAdmin()) {
      fetchSeatPrice();
    }
  }, [seatData?.isOverLimit, fetchSeatPrice]);

  // Handle seat purchase
  const handlePurchaseSeats = async () => {
    const qty = seatData?.excess || 1;
    setPurchasing(true);
    setPurchaseResult(null);
    try {
      const res = await apiPost("/api/plans/account/seats/add", { quantity: qty });
      const data = await res.json();
      if (res.ok && data.success) {
        setPurchaseResult({
          success: true,
          message: `${qty} seat(s) adicionado(s)! Novo limite: ${data.newLimit} usuários.`,
          billing: data.billing,
        });
        // Refresh features and seat status after short delay
        setTimeout(async () => {
          if (refreshFeatures) await refreshFeatures();
          await fetchSeatStatus();
          setShowConfirm(false);
          setPurchaseResult(null);
          // Notifica a página que originou a intenção de criar usuário
          globalThis.dispatchEvent(new CustomEvent("seat:purchased", { detail: { newLimit: data.newLimit } }));
        }, 2500);
      } else {
        setPurchaseResult({
          success: false,
          message: data.message || data.error || "Falha ao adicionar seats",
        });
      }
    } catch (err) {
      // 402 é interceptado pelo apiFetch como billing:blocked —
      // mas aqui estamos tentando comprar seats, não é bloqueio de billing.
      const msg = err?.status === 402
        ? "Conta com pendência financeira. Regularize o pagamento antes de comprar seats."
        : err?.data?.error || err?.message || "Erro de conexão";
      setPurchaseResult({
        success: false,
        message: msg,
      });
    } finally {
      setPurchasing(false);
    }
  };

  // SuperAdmins bypass — exceto quando há prompt explícito de upgrade
  // (ex.: clicaram "Novo Usuário" e bateram no limite RESOURCE_LIMIT)
  if (isSuperAdmin() && !seatData?.isUpgradePrompt) return null;

  // Still loading or no data
  if (loading || !seatData) return null;

  // Not over limit — nothing to show (exceto quando é prompt de upgrade proativo)
  if (!seatData.isOverLimit && !seatData.isUpgradePrompt) return null;

  // Check if current user is blocked
  const currentUserId = user?.id;
  const isCurrentUserBlocked = seatData.blockedUserIds?.includes(currentUserId);
  const admin = isAdmin() || isSuperAdmin();

  // If user is not blocked and not admin, nothing to show
  // (they can keep working normally)
  if (!isCurrentUserBlocked && !admin) return null;

  // ══════════════════════════════════════════
  // VARIANT: Blocked non-admin user
  // ══════════════════════════════════════════
  if (isCurrentUserBlocked && !admin) {
    return (
      <div className="fixed inset-0 z-[9998] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative z-10 w-full max-w-md mx-4 bg-surface border border-border-strong rounded-2xl shadow-2xl animate-in zoom-in-95 fade-in duration-300">
          {/* Header */}
          <div className="flex flex-col items-center pt-8 pb-4 px-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-amber-500/15 border-2 border-amber-500/30">
              <UserX className="w-8 h-8 text-amber-700 dark:text-amber-400" />
            </div>

            <h2 className="text-xl font-bold text-foreground-strong text-center">
              Acesso Temporariamente Limitado
            </h2>

            <div className="mt-1 px-3 py-0.5 rounded-full bg-surface-active border border-border-strong">
              <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                Limite de usuários excedido
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 pb-6 space-y-4">
            <p className="text-sm text-foreground-muted text-center leading-relaxed">
              Sua empresa atingiu o limite de usuários do plano atual.
              Seu acesso foi temporariamente suspenso até que um
              administrador ajuste o plano ou remova usuários excedentes.
            </p>

            <div className="flex items-center justify-center gap-2 py-3 px-4 bg-surface-active border border-border-strong rounded-xl">
              <ShieldAlert className="w-5 h-5 text-foreground-muted" />
              <span className="text-sm text-foreground-muted">
                Contate o administrador da sua empresa
              </span>
            </div>

            <p className="text-xs text-foreground-muted/60 text-center">
              Seu acesso será restaurado automaticamente assim que a situação for regularizada.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════
  // VARIANT: Admin view (sees blocked users)
  // Admin can buy extra seats or dismiss to manage
  // ══════════════════════════════════════════
  if (admin) {
    // Admin dismissed the dialog to go manage users/plan — let them work
    if (dismissed) return null;

    const handleManageUsers = () => {
      setDismissed(true);
      // Sem conta resolvida não há tela de usuários para abrir; o dashboard
      // decide o destino. (O fallback anterior mandava para billing, que não
      // tem relação com gerenciar assentos.)
      navigate(
        user?.accountId ? accountPath(user.accountId, "users") : "/internal/dashboard",
      );
    };

    const excessQty = seatData.excess || 1;
    const totalPurchasePrice = seatPrice ? seatPrice * excessQty : null;
    const isUpgradePrompt = !!seatData.isUpgradePrompt;
    const hasBlockedUsers = (seatData.blockedUsers?.length || 0) > 0;

    // Base do plano x seats extras contratados (para uma mensagem mais intuitiva)
    const planBase = Number.isFinite(seatData.planBaseLimit)
      ? seatData.planBaseLimit
      : null;
    const extras = Number.isFinite(seatData.extras)
      ? seatData.extras
      : planBase != null
        ? Math.max(0, (seatData.limit || 0) - planBase)
        : 0;
    const moreUsersLabel = excessQty === 1 ? "1 usuário" : `${excessQty} usuários`;
    const addSeatsLabel =
      excessQty === 1 ? "um seat extra" : `${excessQty} seats extras`;
    const extrasLabel = extras === 1 ? "1 seat extra" : `${extras} seats extras`;

    return (
      <div className="fixed inset-0 z-[9998] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative z-10 w-full max-w-lg mx-4 bg-surface border border-border-strong rounded-2xl shadow-2xl animate-in zoom-in-95 fade-in duration-300">
          {/* Header */}
          <div className="flex flex-col items-center pt-8 pb-4 px-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-red-500/15 border-2 border-red-500/30">
              <Users className="w-8 h-8 text-red-700 dark:text-red-400" />
            </div>

            <h2 className="text-xl font-bold text-foreground-strong text-center">
              {isUpgradePrompt && !hasBlockedUsers
                ? "Limite de Usuários Atingido"
                : "Limite de Usuários Excedido"}
            </h2>

            <div className="mt-2 flex items-center gap-2">
              <div className="px-3 py-0.5 rounded-full bg-surface-active border border-border-strong">
                <span className="text-xs font-medium text-red-700 dark:text-red-400">
                  {seatData.current} / {seatData.limit} usuários
                </span>
              </div>
              <div className="px-3 py-0.5 rounded-full bg-red-500/10 border border-red-500/30">
                <span className="text-xs font-medium text-red-700 dark:text-red-400">
                  {seatData.excess} excedente{seatData.excess > 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 pb-6 space-y-4">
            <p className="text-sm text-foreground-muted text-center leading-relaxed">
              {isUpgradePrompt && !hasBlockedUsers ? (
                planBase != null && extras > 0 ? (
                  <>
                    Seu plano inclui <strong>{planBase}</strong> usuários e você já
                    adicionou <strong>{extrasLabel}</strong>, todos em uso. Para criar
                    mais {moreUsersLabel}, adicione {addSeatsLabel}.
                  </>
                ) : (
                  <>
                    Seu plano permite <strong>{seatData.limit}</strong> usuários e todos
                    já estão em uso. Para criar mais {moreUsersLabel},
                    adicione {addSeatsLabel}.
                  </>
                )
              ) : (
                <>
                  Seu plano permite até <strong>{seatData.limit}</strong> usuários,
                  mas sua conta possui <strong>{seatData.current}</strong> ativos.
                  Os seguintes usuários estão com acesso bloqueado:
                </>
              )}
            </p>

            {/* Blocked users list */}
            {seatData.blockedUsers?.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-xl border border-border-strong bg-surface-active/50">
                {seatData.blockedUsers.map((bu, idx) => (
                  <div
                    key={bu.id}
                    className={`flex items-center gap-3 px-4 py-2.5 ${
                      idx > 0 ? "border-t border-border" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
                      <UserX className="w-4 h-4 text-red-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-strong truncate">
                        {bu.fullName}
                      </p>
                      <p className="text-xs text-foreground-muted truncate">
                        {bu.email}
                      </p>
                    </div>
                    <span className="text-xs text-foreground-muted/60 shrink-0">
                      {bu.roleName || "Agente"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* ── Confirm purchase sub-dialog ── */}
            {showConfirm && (
              <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                <h3 className="text-sm font-semibold text-foreground-strong flex items-center gap-2">
                  <Plus className="w-4 h-4 text-cyan-600" />
                  Adicionar {excessQty} seat{excessQty > 1 ? "s" : ""}
                </h3>

                {totalPurchasePrice != null ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground-muted">Preço por seat</span>
                      <span className="font-medium text-foreground-strong">
                        R$ {seatPrice.toFixed(2)}/mês
                      </span>
                    </div>
                    {excessQty > 1 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground-muted">Quantidade</span>
                        <span className="font-medium text-foreground-strong">
                          {excessQty}x
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                      <span className="font-semibold text-foreground-strong">Total adicional</span>
                      <span className="font-bold text-cyan-600">
                        R$ {totalPurchasePrice.toFixed(2)}/mês
                      </span>
                    </div>
                    <p className="text-xs text-foreground-muted/70">
                      O valor será adicionado à sua assinatura com cobrança pro-rata.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-amber-600">
                    Preço de seat adicional não configurado. Contate o suporte.
                  </p>
                )}

                {/* Purchase result feedback */}
                {purchaseResult && (
                  <div
                    className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                      purchaseResult.success
                        ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400"
                        : "bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400"
                    }`}
                  >
                    {purchaseResult.success ? (
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    )}
                    <span>{purchaseResult.message}</span>
                  </div>
                )}

                {/* Confirm / Cancel buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowConfirm(false);
                      setPurchaseResult(null);
                    }}
                    disabled={purchasing}
                    className="flex-1 py-2 px-3 text-sm font-medium rounded-lg border border-border-strong bg-surface hover:bg-surface-hover text-foreground-strong transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handlePurchaseSeats}
                    disabled={purchasing || !totalPurchasePrice || purchaseResult?.success}
                    className="flex-1 py-2 px-3 text-sm font-semibold rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {purchasing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processando...
                      </>
                    ) : purchaseResult?.success ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Concluído
                      </>
                    ) : (
                      "Confirmar Compra"
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            {!showConfirm && (
              <div className="flex flex-col gap-2">
                {/* Primary: Add seats (only if price is available) */}
                {priceLoading ? (
                  <div className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-surface-active border border-border-strong rounded-xl">
                    <Loader2 className="w-4 h-4 animate-spin text-foreground-muted" />
                    <span className="text-sm text-foreground-muted">Verificando preço...</span>
                  </div>
                ) : seatPrice ? (
                  <button
                    onClick={() => setShowConfirm(true)}
                    className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-900/20"
                  >
                    <Plus className="w-5 h-5" />
                    Adicionar {excessQty} Seat{excessQty > 1 ? "s" : ""} — R$ {(seatPrice * excessQty).toFixed(2)}/mês
                  </button>
                ) : (
                  <button
                    onClick={handleManageUsers}
                    className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-900/20"
                  >
                    <Settings className="w-5 h-5" />
                    Gerenciar Usuários
                  </button>
                )}

                {/* Secondary: Manage users */}
                {seatPrice && (
                  <button
                    onClick={handleManageUsers}
                    className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-surface-active hover:bg-surface-hover border border-border-strong text-foreground-strong font-medium rounded-xl transition-all"
                  >
                    <Settings className="w-5 h-5" />
                    Gerenciar Usuários
                  </button>
                )}
              </div>
            )}

            {/* Auto-refresh hint */}
            <div className="flex items-center justify-center gap-1.5 text-xs text-foreground-muted/60">
              <RefreshCw className="w-3 h-3" />
              Atualiza automaticamente quando a situação for regularizada
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
